import React, { Fragment } from 'react';

import { margin, useClassnames } from '@lumapps/classnames';
import {
    GenericEntityBlockList,
    GenericEntityBlockListProps,
} from '@lumapps/lumx-blocks/components/GenericEntityBlockList';
import { SearchField } from '@lumapps/lumx-filter-and-sort/components/SearchField';
import { FlexBox, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { MinimumViableUser } from '../../types';
import { UserBlock, UserBlockProps, UserBlockSkeleton } from '../UserBlock';

import './index.scss';

export interface UserListProps
    extends Omit<
        GenericEntityBlockListProps<MinimumViableUser<'id' | 'fullName'>>,
        'entities' | 'getEntityId' | 'Block' | 'Skeleton' | 'scope'
    > {
    /** The callback to handle a user clicking the "load more" button. When defined, the button is displayed at the bottom */
    onLoadMore?(): void;
    /** The callback to handle a user using the search field. When defined a search field is displayed at the top */
    onSearch?(searchText: string): void;
    /** The callback to handle a user clicking the "clear" button on the search field */
    onSearchClear?(): void;
    /** The current value of the search field (note that the onSearch callback should be defined too) */
    searchText?: string;
    /** The array of users to display */
    users?: MinimumViableUser<'id' | 'fullName'>[];
    /** additional props to be passed into the user block */
    getAdditionalUserBlockProps?: (user: MinimumViableUser<'id' | 'fullName'>) => Partial<UserBlockProps>;
    /** scope for tracking purposes */
    scope?: string;
    /** spacing between users */
    itemPadding?: 'regular' | 'big';
}

const UserBlockWrapper =
    (getAdditionalUserBlockProps?: UserListProps['getAdditionalUserBlockProps']) =>
    (user: MinimumViableUser<'id' | 'fullName'>) => <UserBlock {...getAdditionalUserBlockProps?.(user)} user={user} />;

/**
 * This component displays a list of users by reusing the userBlock
 * (profile picture, name, email/primary/secondary fields)
 *
 * Several options are available
 * - a skeleton, if the list is loading (i.e. isLoading is true), there are two cases:
 *       1. only a skeleton when users is empty
 *       2. a skeleton after the list when loading more, i.e. users is not empty
 * - a load more button (optional, you have to pass onLoadMore callback for it to be displayed)
 *       NB: to manage the button disappearing, simply pass undefined in onLoadMore once there is no more elements.
 * - a search field (optional, you have to pass onSearch callback for it to be displayed)
 * - dividers to display the users separated
 */
export const UserList: React.FC<UserListProps> = ({
    onSearch,
    onSearchClear,
    searchText,
    users,
    getAdditionalUserBlockProps,
    scope = 'user-list',
    itemPadding = 'big',
    ...forwardedProps
}) => {
    const { translateKey } = useTranslate();
    const { element, block } = useClassnames('user-list');

    return (
        <>
            {onSearch && (
                <FlexBox fillSpace className={element('search-field', [margin('all', 'huge')])}>
                    <SearchField
                        label={translateKey(GLOBAL.SEARCH_FOR_SOMEONE)}
                        value={searchText}
                        onSearch={onSearch}
                        textFieldProps={{ onClear: onSearchClear }}
                    />
                </FlexBox>
            )}

            <GenericEntityBlockList
                entities={users}
                getEntityId={(user) => user.id}
                Block={UserBlockWrapper(getAdditionalUserBlockProps)}
                Skeleton={<UserBlockSkeleton hasAdditionalInformation={false} className={element('skeleton')} />}
                {...forwardedProps}
                className={block({ [`item-padding-${itemPadding}`]: true }, [forwardedProps.className])}
                loadMoreButtonProps={{
                    label: translateKey(GLOBAL.SHOW_MORE),
                    fullWidth: true,
                    size: Size.m,
                    isLoading: forwardedProps.isLoading,
                    ...forwardedProps.loadMoreButtonProps,
                }}
                iconSize={forwardedProps.iconSize || Size.l}
                scope={scope}
            />
        </>
    );
};
